import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DashboardField, ManifestLayer } from "../types/contract";
import { useFieldLayerList, useLayerData } from "./queries";
import { layersFromDocument, mergeDetail } from "../lib/layerLoad";
import {
  computePresetLayers,
  defaultSeason,
  presetNeedsSeasons,
  type PresetInputs,
  type FertPreset,
} from "../lib/fertPreset";

/*
 *
 * THE BASE layer — the one the card's CLICK makes visible (`defaultVisible: idx === 0`) and the one
 * removal lived on that separate layer; since removal moved INSIDE the rate the total is only built
 * for `k-genius` on a field whose diffusion-K layer is missing, where `withCation` turns on.
 */
export function previewLayerFor(
  layers: ManifestLayer[],
  nutrient: string,
): ManifestLayer | null {
  return (
    layers.find((l) => l.id === `fert-${nutrient}`) ??
    layers.find((l) => l.default_visible) ??
    null
  );
}

// field-average of the Rx surface (mean of the per-cell `value`) — for the card info box
function layerAvg(layer: ManifestLayer | null): number | null {
  const fc = layer?.inline as GeoJSON.FeatureCollection | undefined;
  if (!fc?.features?.length) return null;
  let sum = 0;
  let n = 0;
  for (const f of fc.features) {
    const v = (f.properties as { value?: unknown })?.value;
    if (typeof v === "number" && Number.isFinite(v)) {
      sum += v;
      n++;
    }
  }
  return n ? sum / n : null;
}

// CLIENT-compute is only the FALLBACK when no served layer exists yet (a field whose AutoRx hasn't
// been generated).
export function useAutoRxLayer(
  field: DashboardField,
  preset: FertPreset,
  inputs: PresetInputs,
  inputsReady: boolean,
  enabled: boolean,
): {
  layer: ManifestLayer | null;
  avg: number | null;
  unit: string;
  loading: boolean;
  error: boolean;
} {
  const { data: listed } = useFieldLayerList(field.field_id);
  const manifest = useMemo(
    () => (listed ? layersFromDocument(listed.document) : undefined),
    [listed],
  );
  const servedMeta = manifest?.find((l) => l.id === `autorx-${preset.id}`);
  const detail = useLayerData(
    field.field_id,
    servedMeta?.id ?? null,
    servedMeta?.content_version,
    enabled && !!servedMeta,
  );
  const served = servedMeta ? mergeDetail(servedMeta, detail.data) : undefined;
  const useServed = enabled && !!servedMeta;

  // served layer: use inline directly; fetch the FC only if it came as a signed_url. THROW on any
  // failure (bad status / non-JSON) so react-query settles to isError instead of caching a null
  // "success" that would leave the preview stuck on a spinner forever.
  const servedNeedsFetch =
    useServed && !served?.inline && Boolean(served?.signed_url);
  const {
    data: servedFetched,
    isLoading: servedLoading,
    isError: servedFetchError,
  } = useQuery({
    enabled: servedNeedsFetch,
    queryKey: [
      "autorx-served",
      field.field_id,
      preset.id,
      served?.content_version,
    ],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<ManifestLayer> => {
      const r = await fetch(served!.signed_url as string);
      if (!r.ok) throw new Error(`autorx served ${r.status}`);
      const fc = await r.json();
      return { ...served!, inline: fc } as ManifestLayer;
    },
  });
  // a served layer with neither inline nor a signed_url can't be loaded — treat as an error, not a spinner
  const servedMissing =
    useServed &&
    !detail.isPending &&
    !detail.isError &&
    !served?.inline &&
    !served?.signed_url;
  const servedLayer: ManifestLayer | null = useServed
    ? served?.inline
      ? served!
      : (servedFetched ?? null)
    : null;

  // Only P still needs a season here.
  const seasons = useMemo(
    () => (presetNeedsSeasons(preset) ? [defaultSeason("corn")] : []),
    [preset],
  );
  // bust the cache on a RE-MAP (new modeled maps → new content_versions)
  const mlVersion = useMemo(
    () =>
      (manifest ?? [])
        .filter((l) => (l.id ?? "").startsWith("ml-"))
        .map((l) => l.content_version)
        .join("|"),
    [manifest],
  );
  const {
    data: computed,
    isLoading: computing,
    isError,
  } = useQuery({
    enabled: enabled && !useServed && inputsReady && Boolean(manifest),
    queryKey: ["autorx", field.field_id, preset.id, mlVersion],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<ManifestLayer | null> => {
      const c = await computePresetLayers(inputs, preset, seasons);
      if (!c.ok) throw new Error(c.error ?? "no Rx");
      return previewLayerFor(c.layers, preset.nutrient);
    },
  });

  const layer = useServed ? servedLayer : (computed ?? null);
  const avg = useMemo(() => layerAvg(layer), [layer]);
  // served: inline is instant (no loading); a signed_url is loading only while its fetch is pending, and
  // errors if that fetch failed or the layer is unloadable. compute: loading until the rec resolves.
  const loading = !enabled
    ? false
    : useServed
      ? detail.isPending || (servedNeedsFetch ? servedLoading : false)
      : computing || !inputsReady;
  const error = !enabled
    ? false
    : useServed
      ? detail.isError || (servedNeedsFetch ? servedFetchError : servedMissing)
      : isError;
  return { layer, avg, unit: layer?.unit ?? "", loading, error };
}
